import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ComicCard from "../../components/feature/ComicCard";
import { comics, hotTags } from "../../mocks/manga";

type SortKey = "hot" | "rating" | "new";

const allTags = Array.from(new Set([...hotTags, ...comics.flatMap((c) => c.tags)]));

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "妖精漫画网 漫画大全",
  url: "https://readdiary.github.io/library",
  description: "浏览全部漫画作品，按标签筛选、搜索与排序，找到你喜欢的漫画。",
  mainEntity: {
    "@type": "ItemList",
    itemListElement: comics.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.title,
      url: `https://readdiary.github.io/detail/${c.id}`,
    })),
  },
};

const PAGE_SIZE = 12;

export default function Library() {
  const [params, setParams] = useSearchParams();
  const initialTag = params.get("tag") || "全部";
  const initialKeyword = params.get("keyword") || "";

  const [tag, setTag] = useState(initialTag);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setTag(params.get("tag") || "全部");
    setKeyword(params.get("keyword") || "");
    setPage(1);
  }, [params]);

  const filtered = useMemo(() => {
    let list = [...comics];
    if (tag !== "全部") {
      list = list.filter((c) => c.tags.includes(tag));
    }
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (sort === "new") list.sort((a, b) => Number(b.year) - Number(a.year));
    else list.sort((a, b) => Number(b.views.replace("万", "")) - Number(a.views.replace("万", "")));
    return list;
  }, [tag, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const selectTag = (t: string) => {
    setTag(t);
    setParams(t === "全部" ? {} : { tag: t });
    setPage(1);
  };

  const handleKeywordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams(keyword.trim() ? { keyword: keyword.trim() } : {});
    setPage(1);
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />

      <main>
        <section className="border-b border-background-200/70 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <nav aria-label="面包屑" className="mb-4 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-700">漫画大全</span>
            </nav>
            <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">漫画大全</h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              按标签筛选、关键词搜索，轻松找到你喜欢的漫画。全部作品支持阅读、收藏、下载与书单整理。
            </p>

            <form onSubmit={handleKeywordSubmit} role="search" className="mt-6 flex max-w-xl items-center gap-2 rounded-full border border-background-300/70 bg-background-50 px-4 py-2">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画、作者或标签"
                aria-label="搜索漫画"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-1.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                搜索
              </button>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg text-foreground-950">
              共 {filtered.length} 部作品
              {tag !== "全部" && <span className="ml-2 text-sm font-normal text-foreground-500">筛选自“{tag}”</span>}
            </h2>
            <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
              {([["hot", "热门"], ["rating", "评分"], ["new", "最新"]] as [SortKey, string][]).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSort(key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sort === key ? "bg-background-50 font-bold text-primary-700" : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => selectTag("全部")}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                tag === "全部" ? "bg-primary-500 font-bold text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              全部
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => selectTag(t)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  tag === t ? "bg-primary-500 font-bold text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {pageItems.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {pageItems.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-file-search-line text-5xl text-foreground-400" aria-hidden="true"></i>
              <p className="mt-4 font-medium text-foreground-700">没有找到匹配的作品</p>
              <p className="mt-1 text-sm text-foreground-500">换个标签或关键词试试吧</p>
              <button
                type="button"
                onClick={() => {
                  setTag("全部");
                  setKeyword("");
                  setParams({});
                }}
                className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700 disabled:opacity-40"
                aria-label="上一页"
              >
                <i className="ri-arrow-left-line text-sm" aria-hidden="true"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`flex h-9 min-w-9 items-center justify-center rounded-full px-2 text-sm transition-colors ${
                    p === currentPage ? "bg-primary-500 font-bold text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700 disabled:opacity-40"
                aria-label="下一页"
              >
                <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}